<template>
  <div class="compliance-onboarding-wizard">
    <!-- Header -->
    <div class="wizard-header">
      <div class="header-top">
        <router-link to="/compliance-health" class="back-link">
          <i class="fas fa-arrow-left"></i> Back to Dashboard
        </router-link>
      </div>
      <h1><i class="fas fa-rocket"></i> New Compliance Client</h1>
      <p class="wizard-subtitle">
        Onboard a new client in under 5 minutes. Configure organization, insurance requirements, and users — all in one flow.
      </p>
    </div>

    <!-- Progress Indicator -->
    <div class="progress-indicator">
      <div class="progress-steps">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          :class="['progress-step', {
            active: currentStep === index,
            completed: index < currentStep,
            upcoming: index > currentStep
          }]"
          @click="goToStep(index)"
        >
          <div class="step-circle">
            <i v-if="index < currentStep" class="fas fa-check"></i>
            <i v-else :class="step.icon"></i>
          </div>
          <div class="step-label">{{ step.label }}</div>
        </div>
      </div>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: progressWidth }"
        ></div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="wizard-content">

      <!-- ===== STEP 1: Organization & Client ===== -->
      <div v-if="currentStep === 0" class="step-content fade-in">
        <div class="step-header">
          <h2><i class="fas fa-building"></i> Organization & Client</h2>
          <p>Basic information about your new compliance client</p>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label>Client / Organization Name <span class="required">*</span></label>
            <input
              v-model="form.organization.name"
              type="text"
              class="form-control"
              placeholder="ABC Construction Inc."
              @input="autoFillFromName"
            >
          </div>

          <div class="form-group">
            <label>Primary Contact Email <span class="required">*</span></label>
            <input
              v-model="form.organization.primary_contact_email"
              type="email"
              class="form-control"
              placeholder="admin@abcconstruction.com"
            >
          </div>

          <div class="form-group">
            <label>Industry</label>
            <select v-model="form.organization.industry" class="form-control">
              <option value="CONSTRUCTION">Construction</option>
              <option value="INSURANCE">Insurance</option>
              <option value="REAL_ESTATE">Real Estate</option>
              <option value="HEALTHCARE">Healthcare</option>
              <option value="TECHNOLOGY">Technology</option>
              <option value="MANUFACTURING">Manufacturing</option>
              <option value="PROFESSIONAL_SERVICES">Professional Services</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div class="form-group">
            <label>Subscription Tier</label>
            <select v-model="form.organization.subscription_tier" class="form-control">
              <option value="TRIAL">Trial</option>
              <option value="STANDARD">Standard</option>
              <option value="PROFESSIONAL">Professional</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </div>

          <div class="form-group full-width">
            <label>Client Address</label>
            <input
              v-model="form.check_definition.client_address"
              type="text"
              class="form-control"
              placeholder="123 Main St, Suite 100, City, ST 12345"
            >
          </div>

          <div class="form-group full-width">
            <label>Entity Terminology</label>
            <div class="radio-group">
              <label class="radio-label" v-for="term in entityTerminologies" :key="term.value">
                <input type="radio" v-model="form.project.entity_terminology" :value="term.value">
                <span>{{ term.label }}</span>
              </label>
            </div>
            <small class="form-help">What does this client call the companies they track compliance for?</small>
          </div>
        </div>
      </div>

      <!-- ===== STEP 2: Check Definition Template ===== -->
      <div v-if="currentStep === 1" class="step-content fade-in">
        <div class="step-header">
          <h2><i class="fas fa-clipboard-check"></i> Insurance Requirements Template</h2>
          <p>Choose a check definition to clone as the base for this client's requirements</p>
        </div>

        <div v-if="loadingTemplates" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading templates...</p>
        </div>

        <div v-else-if="templates.length === 0" class="empty-state">
          <i class="fas fa-exclamation-triangle"></i>
          <h3>No Templates Available</h3>
          <p>No check definitions are available for cloning. Create one in the Check Lab first.</p>
        </div>

        <div v-else>
          <div class="template-list">
            <div
              v-for="template in templates"
              :key="template.id"
              :class="['template-card', { selected: form.check_definition.source_template_id === template.id }]"
              @click="selectTemplate(template)"
            >
              <div class="template-left">
                <div class="template-icon">
                  <i class="fas fa-shield-alt"></i>
                </div>
                <div class="template-info">
                  <h3>{{ template.name }}</h3>
                  <p class="template-meta">
                    <span class="badge badge-type">{{ template.application_type }}</span>
                    <span class="badge badge-org">{{ template.organization }}</span>
                    <span>{{ template.requirements_count }} requirements</span>
                  </p>
                  <p v-if="template.description" class="template-desc">{{ template.description }}</p>
                </div>
              </div>
              <div class="template-right">
                <i v-if="form.check_definition.source_template_id === template.id" class="fas fa-check-circle selected-icon"></i>
                <span v-if="template.is_template === false" class="badge badge-warn">Not marked as template</span>
              </div>
            </div>
          </div>

          <!-- Template Preview (once selected) -->
          <div v-if="templatePreview" class="template-preview">
            <h3><i class="fas fa-search"></i> Template Preview: {{ templatePreview.name }}</h3>
            <div class="requirements-preview">
              <div v-for="req in templatePreview.requirements.slice(0, 8)" :key="req.id" class="req-preview-item">
                <span class="req-severity" :class="'severity-' + (req.severity || 'medium').toLowerCase()">
                  {{ (req.severity || 'MEDIUM').charAt(0) }}
                </span>
                <span class="req-name">{{ req.name }}</span>
                <span v-if="req.has_limit" class="req-limit">${{ formatNumber(req.current_limit) }}</span>
              </div>
              <div v-if="templatePreview.requirements.length > 8" class="req-preview-more">
                + {{ templatePreview.requirements.length - 8 }} more requirements
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== STEP 3: Customize Requirements ===== -->
      <div v-if="currentStep === 2" class="step-content fade-in">
        <div class="step-header">
          <h2><i class="fas fa-sliders-h"></i> Customize Requirements</h2>
          <p>Set coverage limits and client-specific details. Start with a preset, then override as needed.</p>
        </div>

        <!-- Preset Selection -->
        <div class="preset-section">
          <h3>Coverage Limit Preset</h3>
          <div class="preset-grid">
            <div
              v-for="preset in presets"
              :key="preset.key"
              :class="['preset-card', { selected: form.check_definition.preset === preset.key }]"
              @click="selectPreset(preset)"
            >
              <div class="preset-name">{{ preset.name }}</div>
              <div class="preset-desc">{{ preset.description }}</div>
              <div class="preset-highlights">
                <span v-if="preset.limits.gl_each_occurrence">GL: ${{ formatNumber(preset.limits.gl_each_occurrence) }}</span>
                <span v-if="preset.limits.auto_combined_single">Auto: ${{ formatNumber(preset.limits.auto_combined_single) }}</span>
                <span v-if="preset.limits.umb_each_occurrence">Umb: ${{ formatNumber(preset.limits.umb_each_occurrence) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Limit Overrides -->
        <div class="limits-section" v-if="form.check_definition.preset">
          <h3>Coverage Limits <small>(adjust if different from preset)</small></h3>
          <div class="limits-grid">
            <div v-for="(group, groupName) in limitGroups" :key="groupName" class="limit-group">
              <h4>{{ groupName }}</h4>
              <div v-for="limit in group" :key="limit.key" class="limit-row">
                <label>{{ limit.label }}</label>
                <div class="limit-input-wrapper">
                  <span class="currency-prefix">$</span>
                  <input
                    :value="getEffectiveLimit(limit.key)"
                    @input="setLimitOverride(limit.key, $event.target.value)"
                    type="text"
                    class="form-control limit-input"
                    :placeholder="getPresetLimit(limit.key)"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Named Insured Details -->
        <div class="named-insured-section">
          <h3>Certificate Holder & Additional Insured</h3>
          <div class="form-grid">
            <div class="form-group">
              <label>Certificate Holder Name</label>
              <input
                v-model="form.check_definition.certificate_holder"
                type="text"
                class="form-control"
                :placeholder="form.organization.name"
              >
              <small class="form-help">Defaults to the organization name if left blank</small>
            </div>
            <div class="form-group">
              <label>Additional Insured Name</label>
              <input
                v-model="form.check_definition.additional_insured"
                type="text"
                class="form-control"
                :placeholder="form.organization.name"
              >
            </div>
            <div class="form-group full-width">
              <label>Additional Insured Operations</label>
              <input
                v-model="form.check_definition.additional_insured_operations"
                type="text"
                class="form-control"
                placeholder="ongoing and completed operations"
              >
            </div>
          </div>
        </div>
      </div>

      <!-- ===== STEP 4: Users ===== -->
      <div v-if="currentStep === 3" class="step-content fade-in">
        <div class="step-header">
          <h2><i class="fas fa-users"></i> Team Members</h2>
          <p>Add users who will manage compliance for this client. You can always add more later.</p>
        </div>

        <div class="users-section">
          <div v-for="(user, index) in form.users" :key="index" class="user-card">
            <div class="user-card-header">
              <span class="user-number">#{{ index + 1 }}</span>
              <button v-if="form.users.length > 1" @click="removeUser(index)" class="btn-icon btn-remove" title="Remove user">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
            <div class="user-fields">
              <div class="form-group">
                <label>Email <span class="required">*</span></label>
                <input v-model="user.email" type="email" class="form-control" placeholder="user@company.com">
              </div>
              <div class="form-group">
                <label>First Name</label>
                <input v-model="user.first_name" type="text" class="form-control" placeholder="Jane">
              </div>
              <div class="form-group">
                <label>Last Name</label>
                <input v-model="user.last_name" type="text" class="form-control" placeholder="Doe">
              </div>
              <div class="form-group">
                <label>Role <span class="required">*</span></label>
                <select v-model="user.role" class="form-control">
                  <option value="ORG_ADMIN">Org Admin (full access)</option>
                  <option value="COMPLIANCE_ADMIN">Compliance Admin</option>
                  <option value="ANALYST">Analyst</option>
                  <option value="CLIENT_MANAGER">Client Manager</option>
                  <option value="REVIEWER">Reviewer (read-only)</option>
                </select>
              </div>
            </div>
          </div>

          <button @click="addUser" class="btn-add-user">
            <i class="fas fa-plus"></i> Add Another User
          </button>

          <div class="users-help">
            <i class="fas fa-info-circle"></i>
            Users will receive temporary passwords. They will need to change them on first login.
          </div>
        </div>
      </div>

      <!-- ===== STEP 5: Review & Execute ===== -->
      <div v-if="currentStep === 4" class="step-content fade-in">
        <div class="step-header">
          <h2><i class="fas fa-check-double"></i> Review & Launch</h2>
          <p>Everything looks good? Let's create this client setup.</p>
        </div>

        <!-- Review Cards -->
        <div v-if="!executionResult" class="review-grid">
          <div class="review-card">
            <h3><i class="fas fa-building"></i> Organization</h3>
            <dl>
              <dt>Name</dt><dd>{{ form.organization.name }}</dd>
              <dt>Email</dt><dd>{{ form.organization.primary_contact_email }}</dd>
              <dt>Industry</dt><dd>{{ form.organization.industry }}</dd>
              <dt>Tier</dt><dd>{{ form.organization.subscription_tier }}</dd>
            </dl>
          </div>

          <div class="review-card">
            <h3><i class="fas fa-clipboard-check"></i> Check Definition</h3>
            <dl>
              <dt>Template</dt><dd>{{ selectedTemplateName }}</dd>
              <dt>Preset</dt><dd>{{ form.check_definition.preset || 'Custom' }}</dd>
              <dt>Cert Holder</dt><dd>{{ form.check_definition.certificate_holder || form.organization.name }}</dd>
              <dt>Add'l Insured</dt><dd>{{ form.check_definition.additional_insured || form.organization.name }}</dd>
            </dl>
          </div>

          <div class="review-card">
            <h3><i class="fas fa-folder-open"></i> Project</h3>
            <dl>
              <dt>Name</dt><dd>{{ form.project.name || (form.organization.name + ' - COI Compliance') }}</dd>
              <dt>Type</dt><dd>CERT_REVIEW</dd>
              <dt>Entities called</dt><dd>{{ form.project.entity_terminology }}</dd>
            </dl>
          </div>

          <div class="review-card">
            <h3><i class="fas fa-users"></i> Users ({{ form.users.length }})</h3>
            <ul class="review-users-list">
              <li v-for="user in form.users" :key="user.email">
                <strong>{{ user.first_name }} {{ user.last_name }}</strong>
                <span class="user-role-badge">{{ user.role }}</span>
                <span class="user-email">{{ user.email }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Execution Progress / Results -->
        <div v-if="executing" class="execution-progress">
          <div class="progress-spinner">
            <i class="fas fa-cog fa-spin fa-3x"></i>
          </div>
          <h3>Creating Client Setup...</h3>
          <p>Setting up organization, cloning check definition, creating users...</p>
        </div>

        <div v-if="executionResult" class="execution-result" :class="{ success: executionResult.success, error: !executionResult.success }">
          <div class="result-header">
            <i :class="executionResult.success ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
            <h3>{{ executionResult.success ? 'Client Successfully Onboarded!' : 'Onboarding Failed' }}</h3>
          </div>

          <div v-if="executionResult.success" class="result-stats">
            <div class="result-stat">
              <div class="stat-value">{{ executionResult.requirements_copied }}</div>
              <div class="stat-label">Requirements Copied</div>
            </div>
            <div class="result-stat">
              <div class="stat-value">{{ executionResult.requirements_customized }}</div>
              <div class="stat-label">Customized</div>
            </div>
            <div class="result-stat">
              <div class="stat-value">{{ executionResult.users.length }}</div>
              <div class="stat-label">Users Created</div>
            </div>
          </div>

          <!-- Validation Checklist -->
          <div v-if="executionResult.validation" class="validation-checklist">
            <h4>Validation Checklist</h4>
            <ul>
              <li v-for="(item, idx) in validationBooleans" :key="idx" :class="{ pass: item.value, fail: !item.value }">
                <i :class="item.value ? 'fas fa-check' : 'fas fa-times'"></i>
                {{ formatValidationKey(item.key) }}
              </li>
            </ul>
          </div>

          <!-- User Credentials -->
          <div v-if="executionResult.users && executionResult.users.some(u => u.temp_password)" class="credentials-section">
            <h4><i class="fas fa-key"></i> User Credentials</h4>
            <p class="credentials-warning">
              <i class="fas fa-exclamation-triangle"></i>
              Save these passwords now — they won't be shown again!
            </p>
            <table class="credentials-table">
              <thead>
                <tr><th>Email</th><th>Role</th><th>Temp Password</th></tr>
              </thead>
              <tbody>
                <tr v-for="user in executionResult.users.filter(u => u.temp_password)" :key="user.email">
                  <td>{{ user.email }}</td>
                  <td>{{ user.role }}</td>
                  <td><code>{{ user.temp_password }}</code></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Error Details -->
          <div v-if="!executionResult.success && executionResult.errors" class="error-details">
            <h4>Error Details</h4>
            <pre>{{ executionResult.errors.join('\n') }}</pre>
          </div>

          <!-- Next Steps -->
          <div v-if="executionResult.success" class="next-steps">
            <h4>Next Steps</h4>
            <ol>
              <li>Share credentials with the client's admin</li>
              <li>Import vendor/subcontractor entities (CSV or manual)</li>
              <li>Test by uploading a sample COI document</li>
              <li>Verify check results match expected outcomes</li>
            </ol>
            <div class="next-steps-actions">
              <button @click="goToProject" class="btn btn-primary">
                <i class="fas fa-folder-open"></i> Go to Project
              </button>
              <button @click="startAnother" class="btn btn-secondary">
                <i class="fas fa-plus"></i> Onboard Another Client
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="wizard-navigation" v-if="!executionResult">
      <button
        @click="previousStep"
        class="btn btn-secondary"
        :disabled="currentStep === 0 || executing"
      >
        <i class="fas fa-arrow-left"></i> Previous
      </button>

      <div class="step-indicator-mini">
        Step {{ currentStep + 1 }} of {{ steps.length }}
      </div>

      <button
        v-if="currentStep < steps.length - 1"
        @click="nextStep"
        class="btn btn-primary"
        :disabled="!canProceed"
      >
        Next <i class="fas fa-arrow-right"></i>
      </button>

      <button
        v-if="currentStep === steps.length - 1"
        @click="executeOnboarding"
        class="btn btn-success btn-lg"
        :disabled="executing || !canProceed"
      >
        <i v-if="!executing" class="fas fa-rocket"></i>
        <i v-else class="fas fa-spinner fa-spin"></i>
        {{ executing ? 'Creating...' : 'Launch Client Setup' }}
      </button>
    </div>

    <!-- Toast Notification -->
    <transition name="toast">
      <div v-if="toast" class="toast-notification" :class="toast.type">
        <i :class="toastIcon"></i>
        {{ toast.message }}
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'ComplianceOnboardingWizard',

  data() {
    return {
      currentStep: 0,
      steps: [
        { id: 'org', label: 'Organization', icon: 'fas fa-building' },
        { id: 'template', label: 'Template', icon: 'fas fa-clipboard-check' },
        { id: 'customize', label: 'Customize', icon: 'fas fa-sliders-h' },
        { id: 'users', label: 'Users', icon: 'fas fa-users' },
        { id: 'review', label: 'Launch', icon: 'fas fa-rocket' },
      ],

      // Form data
      form: {
        organization: {
          name: '',
          primary_contact_email: '',
          industry: 'CONSTRUCTION',
          subscription_tier: 'PROFESSIONAL',
        },
        client: {
          name: '',
          industry_type: 'INSURANCE',
        },
        project: {
          name: '',
          type: 'CERT_REVIEW',
          entity_terminology: 'vendors',
        },
        check_definition: {
          source_template_id: null,
          name: '',
          application_type: 'COI_COMPLIANCE',
          preset: '',
          client_name: '',
          client_address: '',
          certificate_holder: '',
          additional_insured: '',
          additional_insured_operations: 'ongoing and completed operations',
          limit_overrides: {},
        },
        users: [
          { email: '', role: 'ORG_ADMIN', first_name: '', last_name: '' },
        ],
      },

      entityTerminologies: [
        { value: 'vendors', label: 'Vendors' },
        { value: 'subcontractors', label: 'Subcontractors' },
        { value: 'contractors', label: 'Contractors' },
        { value: 'tenants', label: 'Tenants' },
        { value: 'partners', label: 'Partners' },
        { value: 'providers', label: 'Providers' },
      ],

      // Coverage limit groups for display
      limitGroups: {
        'General Liability': [
          { key: 'gl_each_occurrence', label: 'Each Occurrence' },
          { key: 'gl_general_aggregate', label: 'General Aggregate' },
          { key: 'gl_products_completed_ops', label: 'Products/Completed Ops' },
          { key: 'gl_personal_advertising', label: 'Personal & Advertising Injury' },
          { key: 'gl_damage_rented_premises', label: 'Damage to Rented Premises' },
          { key: 'gl_medical_expense', label: 'Medical Expense' },
        ],
        'Workers Compensation': [
          { key: 'wc_each_accident', label: 'Each Accident' },
          { key: 'wc_disease_policy', label: 'Disease - Policy Limit' },
          { key: 'wc_disease_each', label: 'Disease - Each Employee' },
        ],
        'Auto Liability': [
          { key: 'auto_combined_single', label: 'Combined Single Limit' },
        ],
        'Umbrella / Excess': [
          { key: 'umb_each_occurrence', label: 'Each Occurrence' },
          { key: 'umb_aggregate', label: 'Aggregate' },
        ],
      },

      // Loaded data
      templates: [],
      loadingTemplates: false,
      templatePreview: null,
      presets: [],

      // Execution state
      executing: false,
      executionResult: null,
      toast: null,
    }
  },

  computed: {
    progressWidth() {
      return `${((this.currentStep + 1) / this.steps.length) * 100}%`
    },

    selectedTemplateName() {
      const t = this.templates.find(t => t.id === this.form.check_definition.source_template_id)
      return t ? t.name : '(none selected)'
    },

    canProceed() {
      switch (this.currentStep) {
        case 0:
          return !!(
            this.form.organization.name.trim() &&
            this.form.organization.primary_contact_email.trim()
          )
        case 1:
          return !!this.form.check_definition.source_template_id
        case 2:
          return !!this.form.check_definition.preset
        case 3:
          return this.form.users.every(u => u.email.trim() && u.role)
        case 4:
          return true
        default:
          return false
      }
    },

    validationBooleans() {
      if (!this.executionResult || !this.executionResult.validation) return []
      return Object.entries(this.executionResult.validation)
        .filter(([, value]) => typeof value === 'boolean')
        .map(([key, value]) => ({ key, value }))
    },

    toastIcon() {
      if (!this.toast) return ''
      const icons = {
        success: 'fas fa-check-circle',
        error: 'fas fa-exclamation-circle',
        info: 'fas fa-info-circle',
      }
      return icons[this.toast.type] || icons.info
    },
  },

  async mounted() {
    await Promise.all([
      this.loadTemplates(),
      this.loadPresets(),
    ])
  },

  methods: {
    // --- Data Loading ---
    async loadTemplates() {
      this.loadingTemplates = true
      try {
        const response = await api.get('/compliance-onboarding/templates/')
        this.templates = response.data.templates || []
      } catch (error) {
        console.error('Failed to load templates:', error)
        this.showToast('error', 'Failed to load check definition templates')
      } finally {
        this.loadingTemplates = false
      }
    },

    async loadPresets() {
      try {
        const response = await api.get('/compliance-onboarding/presets/')
        this.presets = response.data.presets || []
      } catch (error) {
        console.error('Failed to load presets:', error)
      }
    },

    async loadTemplatePreview(templateId) {
      try {
        const response = await api.get(`/compliance-onboarding/templates/${templateId}/`)
        this.templatePreview = response.data
      } catch (error) {
        console.error('Failed to load template preview:', error)
      }
    },

    // --- Form Helpers ---
    autoFillFromName() {
      const name = this.form.organization.name.trim()
      if (name) {
        this.form.client.name = name
        this.form.check_definition.client_name = name
        if (!this.form.project.name || this.form.project.name === this._lastAutoProjectName) {
          this.form.project.name = `${name} - COI Compliance`
          this._lastAutoProjectName = this.form.project.name
        }
        if (!this.form.check_definition.name) {
          this.form.check_definition.name = `${name} Insurance Requirements`
        }
      }
    },

    selectTemplate(template) {
      this.form.check_definition.source_template_id = template.id
      this.loadTemplatePreview(template.id)
    },

    selectPreset(preset) {
      this.form.check_definition.preset = preset.key
      // Clear overrides when switching preset
      this.form.check_definition.limit_overrides = {}
    },

    getPresetLimit(key) {
      if (!this.form.check_definition.preset) return ''
      const preset = this.presets.find(p => p.key === this.form.check_definition.preset)
      if (preset && preset.limits[key] !== undefined) {
        return this.formatNumber(preset.limits[key])
      }
      return ''
    },

    getEffectiveLimit(key) {
      // Override takes precedence
      if (this.form.check_definition.limit_overrides[key] !== undefined) {
        return this.formatNumber(this.form.check_definition.limit_overrides[key])
      }
      return this.getPresetLimit(key)
    },

    setLimitOverride(key, rawValue) {
      const numValue = parseInt(rawValue.replace(/[^0-9]/g, ''), 10)
      if (!isNaN(numValue)) {
        this.form.check_definition.limit_overrides[key] = numValue
      } else {
        // Clear override to fall back to preset
        delete this.form.check_definition.limit_overrides[key]
      }
    },

    addUser() {
      this.form.users.push({ email: '', role: 'ANALYST', first_name: '', last_name: '' })
    },

    removeUser(index) {
      this.form.users.splice(index, 1)
    },

    // --- Navigation ---
    nextStep() {
      if (this.canProceed && this.currentStep < this.steps.length - 1) {
        this.currentStep++
      }
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },

    goToStep(index) {
      // Only allow going to completed steps or the current step
      if (index <= this.currentStep) {
        this.currentStep = index
      }
    },

    // --- Execution ---
    async executeOnboarding() {
      this.executing = true
      this.executionResult = null

      try {
        // Fill in defaults for missing fields
        const payload = JSON.parse(JSON.stringify(this.form))
        if (!payload.client.name) payload.client.name = payload.organization.name
        if (!payload.project.name) payload.project.name = `${payload.organization.name} - COI Compliance`
        if (!payload.check_definition.name) payload.check_definition.name = `${payload.organization.name} Insurance Requirements`
        if (!payload.check_definition.client_name) payload.check_definition.client_name = payload.organization.name
        if (!payload.check_definition.certificate_holder) {
          payload.check_definition.certificate_holder = payload.organization.name
        }
        if (!payload.check_definition.additional_insured) {
          payload.check_definition.additional_insured = payload.organization.name
        }

        const response = await api.post('/compliance-onboarding/execute/', payload)
        this.executionResult = response.data

        if (this.executionResult.success) {
          this.showToast('success', 'Client onboarded successfully!')
        } else {
          this.showToast('error', 'Onboarding completed with issues')
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Onboarding failed'
        this.executionResult = {
          success: false,
          errors: [errorMsg],
        }
        this.showToast('error', errorMsg)
        console.error('Onboarding execution error:', error)
      } finally {
        this.executing = false
      }
    },

    goToProject() {
      if (this.executionResult?.project?.id) {
        this.$router.push(`/compliance-health`)
      }
    },

    startAnother() {
      // Reset form
      this.currentStep = 0
      this.executionResult = null
      this.templatePreview = null
      this.form = {
        organization: { name: '', primary_contact_email: '', industry: 'CONSTRUCTION', subscription_tier: 'PROFESSIONAL' },
        client: { name: '', industry_type: 'INSURANCE' },
        project: { name: '', type: 'CERT_REVIEW', entity_terminology: 'vendors' },
        check_definition: {
          source_template_id: null, name: '', application_type: 'COI_COMPLIANCE',
          preset: '', client_name: '', client_address: '', certificate_holder: '',
          additional_insured: '', additional_insured_operations: 'ongoing and completed operations',
          limit_overrides: {},
        },
        users: [{ email: '', role: 'ORG_ADMIN', first_name: '', last_name: '' }],
      }
    },

    // --- Formatting ---
    formatNumber(num) {
      if (num === undefined || num === null || num === '') return ''
      return Number(num).toLocaleString()
    },

    formatValidationKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    showToast(type, message) {
      this.toast = { type, message }
      setTimeout(() => { this.toast = null }, 5000)
    },
  },
}
</script>

<style scoped>
/* ===== Layout ===== */
.compliance-onboarding-wizard {
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px;
  min-height: 100vh;
}

/* ===== Header ===== */
.wizard-header {
  text-align: center;
  margin-bottom: 36px;
}

.header-top {
  text-align: left;
  margin-bottom: 16px;
}

.back-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
}

.back-link:hover {
  text-decoration: underline;
}

.wizard-header h1 {
  color: #1a1a2e;
  font-size: 2.2rem;
  margin: 0 0 8px 0;
  font-weight: 700;
}

.wizard-subtitle {
  color: #6b7280;
  font-size: 1.05rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
}

/* ===== Progress ===== */
.progress-indicator {
  margin-bottom: 32px;
}

.progress-steps {
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 16px;
  position: relative;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 140px;
  cursor: pointer;
  transition: opacity 0.3s;
  opacity: 0.4;
}

.progress-step.active {
  opacity: 1;
}

.progress-step.completed {
  opacity: 0.85;
  cursor: pointer;
}

.step-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  font-size: 0.9rem;
  color: #9ca3af;
  transition: all 0.3s;
  border: 3px solid transparent;
}

.progress-step.active .step-circle {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-color: rgba(102, 126, 234, 0.3);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.progress-step.completed .step-circle {
  background: #10b981;
  color: white;
}

.step-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  text-align: center;
}

.progress-bar {
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transition: width 0.4s ease;
  border-radius: 2px;
}

/* ===== Wizard Content ===== */
.wizard-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 40px;
  margin-bottom: 24px;
  min-height: 460px;
}

.step-header {
  text-align: center;
  margin-bottom: 32px;
}

.step-header h2 {
  color: #1a1a2e;
  font-size: 1.6rem;
  margin: 0 0 8px 0;
}

.step-header p {
  color: #6b7280;
  margin: 0;
}

/* ===== Forms ===== */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  font-size: 0.9rem;
}

.required {
  color: #ef4444;
}

.form-control {
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fafafa;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
  background: white;
}

.form-help {
  color: #9ca3af;
  font-size: 0.8rem;
  margin-top: 4px;
}

.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.radio-label:has(input:checked) {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.06);
}

.radio-label input[type="radio"] {
  accent-color: #667eea;
}

/* ===== Template Selection ===== */
.template-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.template-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.template-card:hover {
  border-color: #667eea;
  background: #fafafe;
}

.template-card.selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.04), rgba(118, 75, 162, 0.04));
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.template-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.template-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.template-info h3 {
  margin: 0 0 4px 0;
  font-size: 1rem;
  color: #1a1a2e;
}

.template-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
}

.template-desc {
  font-size: 0.85rem;
  color: #9ca3af;
  margin: 4px 0 0 0;
}

.badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-type {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-org {
  background: #f3f4f6;
  color: #6b7280;
}

.badge-warn {
  background: #fef3c7;
  color: #92400e;
}

.selected-icon {
  color: #667eea;
  font-size: 1.4rem;
}

/* Template Preview */
.template-preview {
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.template-preview h3 {
  margin: 0 0 16px 0;
  font-size: 1rem;
  color: #374151;
}

.requirements-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.req-preview-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #f3f4f6;
}

.req-severity {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.severity-critical { background: #ef4444; }
.severity-high { background: #f97316; }
.severity-medium { background: #eab308; }
.severity-low { background: #6b7280; }

.req-name {
  flex: 1;
  font-size: 0.9rem;
  color: #374151;
}

.req-limit {
  font-weight: 600;
  color: #059669;
  font-size: 0.85rem;
}

.req-preview-more {
  text-align: center;
  color: #9ca3af;
  font-size: 0.85rem;
  padding: 8px;
}

/* ===== Presets ===== */
.preset-section {
  margin-bottom: 32px;
}

.preset-section h3,
.limits-section h3,
.named-insured-section h3 {
  color: #1a1a2e;
  margin: 0 0 16px 0;
  font-size: 1.1rem;
}

.preset-section h3 small,
.limits-section h3 small {
  color: #9ca3af;
  font-weight: 400;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.preset-card {
  padding: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.preset-card:hover {
  border-color: #667eea;
}

.preset-card.selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.04), rgba(118, 75, 162, 0.04));
}

.preset-name {
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.preset-desc {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 12px;
  line-height: 1.4;
}

.preset-highlights {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.preset-highlights span {
  font-size: 0.8rem;
  font-weight: 600;
  color: #059669;
  background: #ecfdf5;
  padding: 2px 8px;
  border-radius: 6px;
}

/* ===== Limits ===== */
.limits-section {
  margin-bottom: 32px;
}

.limits-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.limit-group h4 {
  color: #374151;
  margin: 0 0 12px 0;
  font-size: 0.95rem;
  padding-bottom: 8px;
  border-bottom: 2px solid #f3f4f6;
}

.limit-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 12px;
}

.limit-row label {
  font-size: 0.85rem;
  color: #6b7280;
  flex: 1;
}

.limit-input-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  width: 160px;
}

.currency-prefix {
  position: absolute;
  left: 10px;
  color: #9ca3af;
  font-weight: 600;
}

.limit-input {
  padding-left: 24px !important;
  text-align: right;
  width: 100%;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
}

/* ===== Named Insured ===== */
.named-insured-section {
  padding-top: 24px;
  border-top: 2px solid #f3f4f6;
}

/* ===== Users ===== */
.users-section {
  max-width: 700px;
  margin: 0 auto;
}

.user-card {
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}

.user-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.user-number {
  font-weight: 700;
  color: #667eea;
  font-size: 0.9rem;
}

.user-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.btn-remove {
  color: #ef4444;
}

.btn-remove:hover {
  background: #fef2f2;
}

.btn-add-user {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #ecfdf5;
  color: #059669;
  border: 2px dashed #a7f3d0;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-user:hover {
  background: #d1fae5;
  border-color: #059669;
}

.users-help {
  margin-top: 16px;
  padding: 12px 16px;
  background: #eff6ff;
  border-radius: 8px;
  color: #1d4ed8;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ===== Review ===== */
.review-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.review-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.review-card h3 {
  margin: 0 0 12px 0;
  font-size: 1rem;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.review-card dl {
  margin: 0;
}

.review-card dt {
  font-weight: 600;
  color: #6b7280;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 8px;
}

.review-card dd {
  margin: 2px 0 0 0;
  color: #1a1a2e;
  font-size: 0.95rem;
}

.review-users-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.review-users-list li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.9rem;
}

.review-users-list li:last-child {
  border-bottom: none;
}

.user-role-badge {
  background: #dbeafe;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
}

.user-email {
  color: #9ca3af;
  font-size: 0.8rem;
}

/* ===== Execution ===== */
.execution-progress {
  text-align: center;
  padding: 60px 20px;
}

.progress-spinner {
  color: #667eea;
  margin-bottom: 16px;
}

.execution-result {
  border-radius: 12px;
  padding: 24px;
}

.execution-result.success {
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
}

.execution-result.error {
  background: #fef2f2;
  border: 2px solid #fecaca;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.result-header i {
  font-size: 2rem;
}

.execution-result.success .result-header i { color: #10b981; }
.execution-result.error .result-header i { color: #ef4444; }

.result-header h3 {
  margin: 0;
  font-size: 1.4rem;
}

.result-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
}

.result-stat {
  background: white;
  border-radius: 10px;
  padding: 16px 24px;
  text-align: center;
  flex: 1;
  border: 1px solid #d1fae5;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #059669;
}

.stat-label {
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

/* Validation Checklist */
.validation-checklist {
  margin-bottom: 24px;
}

.validation-checklist h4 {
  margin: 0 0 12px 0;
  color: #374151;
}

.validation-checklist ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.validation-checklist li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
}

.validation-checklist li.pass {
  color: #059669;
  background: rgba(16, 185, 129, 0.08);
}

.validation-checklist li.fail {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
}

/* Credentials */
.credentials-section {
  margin-bottom: 24px;
  background: white;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #fbbf24;
}

.credentials-section h4 {
  margin: 0 0 8px 0;
  color: #92400e;
}

.credentials-warning {
  color: #92400e;
  font-size: 0.85rem;
  margin: 0 0 12px 0;
  padding: 8px 12px;
  background: #fef3c7;
  border-radius: 6px;
}

.credentials-table {
  width: 100%;
  border-collapse: collapse;
}

.credentials-table th,
.credentials-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.9rem;
}

.credentials-table th {
  font-weight: 600;
  color: #6b7280;
  font-size: 0.8rem;
  text-transform: uppercase;
}

.credentials-table code {
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'SF Mono', monospace;
  font-size: 0.85rem;
}

/* Error Details */
.error-details {
  margin-top: 16px;
}

.error-details pre {
  background: white;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  overflow-x: auto;
  color: #ef4444;
}

/* Next Steps */
.next-steps {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #d1fae5;
}

.next-steps h4 {
  margin: 0 0 12px 0;
  color: #374151;
}

.next-steps ol {
  margin: 0 0 20px 0;
  padding-left: 20px;
  color: #6b7280;
  line-height: 1.8;
}

.next-steps-actions {
  display: flex;
  gap: 12px;
}

/* ===== Navigation ===== */
.wizard-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
}

.step-indicator-mini {
  font-size: 0.85rem;
  color: #9ca3af;
  font-weight: 600;
}

/* ===== Buttons ===== */
.btn {
  padding: 10px 22px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-lg {
  padding: 14px 32px;
  font-size: 1.05rem;
}

/* ===== States ===== */
.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.loading-state i,
.empty-state i {
  font-size: 2.5rem;
  display: block;
  margin-bottom: 12px;
}

/* ===== Animations ===== */
.fade-in {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== Toast ===== */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 14px 20px;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 400px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  font-size: 0.9rem;
}

.toast-notification.success { background: #10b981; }
.toast-notification.error { background: #ef4444; }
.toast-notification.info { background: #3b82f6; }

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .compliance-onboarding-wizard {
    padding: 16px;
  }

  .wizard-header h1 {
    font-size: 1.6rem;
  }

  .wizard-content {
    padding: 24px 16px;
  }

  .form-grid,
  .limits-grid,
  .review-grid,
  .preset-grid {
    grid-template-columns: 1fr;
  }

  .user-fields {
    grid-template-columns: 1fr;
  }

  .validation-checklist ul {
    grid-template-columns: 1fr;
  }

  .result-stats {
    flex-direction: column;
  }

  .wizard-navigation {
    flex-direction: column;
    gap: 12px;
  }

  .progress-step {
    max-width: 60px;
  }

  .step-label {
    font-size: 0.7rem;
  }
}
</style>
